/**
 * ProblemTable — 문제(/problem-hub) 목록의 표 모양(계약 §2 problem-search · reply-batch §3-1·§3-2).
 *
 * 순수 프레젠테이션이다 — fetch·context 를 갖지 않는다. 좁은 폭에서는 10컬럼이 물리적으로
 * 못 들어가서, 담는 쪽(ProblemSearchPanel)이 layout prop 으로 이 표와 카드 목록을 갈라 쓴다
 * (ExamPapersV2/PaperTable 과 같은 규칙).
 *
 * 헤더 정렬은 일부러 넣지 않았다. problem-search 파라미터는 필터와 page/per_page 뿐이고
 * sort 가 없다(계약 §2). 정렬을 붙이면 '현재 페이지 20건 안에서만' 도는 정렬이 되는데, 표는
 * 그것을 전체 순서인 양 보여 준다. 계약에 sort 가 생기면 그때 헤더가 정렬을 가질 수 있다.
 *
 * `preview` 는 본문 140자 **평문**이다 — 수식이 `\frac{1}{2}` 같은 LaTeX 원문으로 보인다.
 * 렌더된 본문은 [미리보기]·[열기]에만 있으므로 그 사실을 컬럼 헤더에 적고 행마다 [미리보기]를 둔다.
 */
import React from 'react';
import { FolderOpen, Eye, Sparkles } from 'lucide-react';
// 「본문 요약」 칸의 조판 본문 — 시험지 구성 모달의 목록과 **같은 부품·같은 얼굴**이다.
import ProblemBodyPreview, { LIST_BODY_MAX_SCALE } from '../ExamPapersV2/ProblemBodyPreview';
// 개념 배지 — 이 저장소가 개념을 그리는 정본 모양. 좁은 칸이라 compact.
import { ConceptBadge, matchConcept } from '../ExamPapersV2/conceptBadge';
import { LabelBadges } from '../ExamPapersV2/labelBadge';
import { conceptPairsOf } from '../ExamPapersV2/paperStats';
import {
  getOriginDisplay, getNeedBadges, difficultyBadgeClass,
  getAnalysisStatusDisplay, ANALYSIS_POLLING_STATUSES, DIFFICULTY_LABEL,
} from './problemApi';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품. **이 표의 PID 칸이 그 기준 얼굴이다**
import ProblemIdLink from './ProblemIdLink';

/** 행 액션은 이름이 같은 버튼이 세로로 스무 개 늘어선다 — 읽어 주는 이름에 문제 번호를 넣는다 */
const ACTION_TONE = {
  neutral: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  violet: 'text-violet-700 hover:bg-violet-100',
};

function RowAction({ icon: Icon, label, subject, onClick, tone = 'neutral', disabled = false, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${subject} ${label}`}
      title={title || label}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${ACTION_TONE[tone]}`}
    >
      <Icon aria-hidden="true" className="size-3" />
      {label}
    </button>
  );
}

/** 비어 있는 것 — need_* 4플래그. 하나도 없으면 '완비'(빈칸이면 배지가 안 뜬 것과 구분되지 않는다) */
export function NeedBadges({ item }) {
  const needs = getNeedBadges(item);
  if (needs.length === 0) return <span className="text-[10px] text-gray-300">완비</span>;
  return (
    <span className="flex flex-wrap gap-0.5">
      {needs.map((n) => (
        <span key={n.key} className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${n.cls}`}>{n.label}</span>
      ))}
    </span>
  );
}

/**
 * ── 본문 칸의 폭에는 **바닥이 있다**(2026-08-29) ─────────────────────────────
 * 종전에는 `w-auto` 라 본문이 남는 폭을 통째로 가져갔다. 오너 지시로 좁히는데, 그냥 좁히면
 * 글자까지 함께 작아진다 — 배율이 `min(0.67, 칸너비/423)` 이라 칸이 좁아지는 순간 뒤쪽 항이
 * 이기기 때문이다. 0.67(=12px, 다른 칸과 같은 크기)을 지키는 칸의 하한은
 *
 *   423 × 0.67 = **283.41px**
 *
 * 이고, 그 위 첫 8격자 값이 `min-w-72`(18rem = 288px)다. 그래서 폭은 26% 로 내리되 하한을
 * 288px 로 못박는다 — 좁은 화면에서도 본문 글자가 12px 아래로 내려가지 않는다.
 * 내준 폭은 라벨·개념이 가져간다(둘 다 배지가 여럿이라 넓을수록 줄이 준다).
 */
const COLUMNS = [
  // PID 칸이 출신 배지를 함께 진다(오너 제안 2026-08-29). 출신을 칼럼으로 세우지 않는 이유는
  // **거의 다 빈 칸이기 때문**이다 — dev 백필 실측이 origin 200(AI)=426 · 300(OCR)=221 ·
  // 900(레거시 사람)=60,357 로 **900 이 98.94%** 인데, 900 은 배지를 그리지 않는다
  // (getOriginDisplay 가 null. 그리면 목록 거의 전부가 같은 회색 칩 하나로 덮여 배지가
  //  아무것도 가르지 못하고, 대비도 2.31:1 로 미달이었다 — problemApi 주석).
  // 즉 칼럼 하나가 화면 폭을 쓰면서 99% 는 비어 있었다. 값이 있어도 「OCR 등록」·「AI·변형」
  // 두 종류뿐이라, 같은 「이 문제가 무엇인가」를 말하는 PID 아래가 제자리다.
  { key: 'id', label: 'PID', className: 'min-w-20 w-[8%]' },
  { key: 'preview', label: '본문 요약', className: 'min-w-72 w-[26%]' },
  { key: 'labels', label: '라벨', className: 'min-w-24 w-[13%]' },
  { key: 'concepts', label: '개념', className: 'min-w-24 w-auto' },
  { key: 'difficulty', label: '난이도', className: 'min-w-16 w-[7%] text-center' },
  { key: 'analysis', label: '분석 상태', className: 'min-w-[5rem] w-[8%]' },
  { key: 'author', label: '작성자', className: 'min-w-16 w-[8%]' },
  { key: 'actions', label: '작업', className: 'min-w-[13rem] w-[16%]' },
];

function ProblemTable({
  items = [],
  bodies = null, // problem_id → 조판 본문 HTML(검색 응답의 with_body=1 을 변환한 것)
  dict = null,   // v2 개념 사전 — 개념 배지의 [순번][유형] 재료. 없으면 이름만 선다
  selectedId = null,
  onSelect,
  onPreview,
  onRequestAnalysis,
  analyzingIds = null,
}) {
  // 분석 상태 열은 값이 하나라도 있을 때만 세운다. analysis_status 는 needs_analysis=true 로
  // 부를 때만 조인돼 오므로(reply-batch §3-1), 안 켰을 때는 전 행이 null 이다. 그때 열을 세우면
  // 스무 칸의 '—' 가 "분석 이력이 없다"로 읽힌다 — 조인을 건너뛴 것뿐인데.
  const showAnalysis = items.some((it) => it.analysis_status != null);
  const columns = showAnalysis ? COLUMNS : COLUMNS.filter((c) => c.key !== 'analysis');
  // Set 이든 배열이든 받는다. 문자열로 맞춰 비교한다 — 서버 id(숫자)와 낙관적 갱신(문자열)이 섞인다.
  const analyzing = new Set(Array.from(analyzingIds || []).map(String));

  if (items.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`whitespace-nowrap px-3 py-2 text-left text-xs font-medium tracking-wider text-gray-500 ${c.className}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {items.map((it) => {
            const id = it.problem_id;
            const active = String(id) === String(selectedId);
            const subject = `${id}번 문제`; // 읽어 줄 이름 — 제목이 없는 자료라 번호가 유일한 식별자다
            const origin = getOriginDisplay(it);
            // concept_names 가 세 형태(객체배열·맵·문자열배열)로 온다 — id+이름 쌍으로 흡수하는
            // 정본을 쓴다. 배지가 사전에서 순번·유형을 찾으려면 이름만으로는 안 된다.
            const conceptPairs = conceptPairsOf(it);
            const needs = getNeedBadges(it);
            const inFlight = analyzing.has(String(id));
            const inProgress = ANALYSIS_POLLING_STATUSES.includes(it.analysis_status);
            const st = it.analysis_status == null ? null : getAnalysisStatusDisplay(it.analysis_status);
            return (
              /* 행 사이의 금 — 경계선 gray-100 → gray-200, 칸 여백 py-2 → py-3(오너 지시 2026-08-31).
                 이 표는 한 행이 80px 넘게 선다(본문 칸 64px + 여백). 그런데 라벨·개념 칸은 배지가
                 여러 줄로 쌓여 위아래 행의 배지가 서로 8px 거리에 놓였고, 그 사이를 가르는 것이
                 흰 바탕과 거의 같은 회색 선 하나뿐이라 어느 배지가 어느 문제 것인지 눈으로 못 갈랐다.
                 선은 이 저장소가 카드 테두리에 쓰는 값(gray-200 · 공용 DataGrid 의 divide-gray-200
                 과 같은 값)으로 올리고, 여백은 위아래 4px 씩 벌려 행을 덩어리로 만든다.
                 줄무늬(짝수 행 배경)는 쓰지 않는다 — 이 저장소에 전례가 없고, 선택(bg-primary/15)·
                 hover(bg-primary/10) 와 배경을 놓고 다툰다. */
              <tr
                key={id}
                onClick={() => onSelect && onSelect(id)}
                aria-current={active ? 'true' : undefined}
                className={`cursor-pointer border-b border-gray-200 transition-colors duration-150 last:border-b-0 ${
                  active ? 'bg-primary/15' : 'hover:bg-primary/10'
                }`}
              >
                {/* 번호가 곧 상태다 — 색·굵기·툴팁·링크가 모두 ProblemIdLink 한 곳에 있다.
                    **이 칸이 그 부품의 기준 얼굴**이고 두 메뉴의 나머지 열 자리가 여기를 따른다
                    (오너 지시 2026-09-01). 행 전체도 같은 문제를 열지만 번호는 진짜 <a> 라
                    Ctrl·⌘ 클릭으로 새 탭이 열린다 — 행 클릭에는 없는 길이다. */}
                <td className="whitespace-nowrap p-3">
                  <ProblemIdLink id={id} item={it} />
                  {/* 출신 — 값이 있을 때만 한 단 아래에 붙는다. 없으면 줄 자체가 없다(대다수가 그렇다). */}
                  {origin && (
                    <div className="mt-0.5">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${origin.cls}`}>{origin.text}</span>
                    </div>
                  )}
                </td>
                {/* 본문 — 지면과 같은 렌더러가 만든 HTML 이라 수식·그림이 그대로 나온다
                    (오너 지시 2026-08-29). 종전에는 평문 140자였고, 수식이 `\dfrac{1}{2}` 같은
                    LaTeX 원문으로 보여 무슨 문제인지 알아볼 수 없는 행이 흔했다.
                    compact 을 쓰는 이유는 **표이기 때문**이다: 높이가 h-16 으로 상한이자 하한이라
                    본문 길이가 제각각이어도 행이 계단처럼 되지 않고, 늦게 도착해도 들썩이지 않는다.
                    본문이 없거나 변환 전이면 평문 요약으로 내려앉는다. */}
                {/* h-px — 테이블 셀의 height 는 **최소값**으로 읽혀, 실제로는 행 높이가 내려온다.
                    안쪽 본문 칸이 h-full 로 그 높이를 받아 채운다(fill). 이 한 줄이 없으면
                    h-full 이 기준을 못 찾아 아무 일도 하지 않는다. */}
                <td className="h-px p-3">
                  <ProblemBodyPreview
                    compact
                    fill
                    /* 배율 상한 — 이 칸은 지면 폭(423px)보다 넓어 상한을 안 주면 배율이 1 로
                       걸리고 본문이 지면 값 그대로 18px 로 선다(실측 DOM: transform:scale(1)).
                       옆 칸 글자가 12px 인데 본문만 18px 이면 표의 위계가 뒤집힌다.
                       값과 근거는 ProblemBodyPreview 의 LIST_BODY_MAX_SCALE 에 있다 —
                       좁은 카드 목록(ProblemSearchPanel)이 같은 상수를 쓴다. */
                    maxScale={LIST_BODY_MAX_SCALE}
                    html={bodies?.[String(id)] ?? null}
                    fallback={it.preview || <span className="text-gray-300">본문 미리보기 없음</span>}
                  />
                </td>
                {/* 라벨 — 콤마로 쪼갠 꼬리표 배지(오너 지시 2026-08-29, 목록 카드와 같은 규격).
                    배지의 얼굴과 160px 상한의 실측 근거는 ExamPapersV2/labelBadge 에 있다 — 표·카드·상세·
                    조립 창이 같은 부품을 쓴다(2026-09-02). 이 칸만 빈 값을 「—」로 표시한다(표의 다른 칸과 같이).
                    `table-fixed` 로 칼럼을 못박는 길도 있으나 그건 여덟 칼럼 전부의 폭 규칙을 바꾸는 일이라,
                    배지 하나에 상한을 두는 쪽을 골랐다. */}
                <td className="p-3 text-xs text-gray-600">
                  <LabelBadges labels={it.labels} empty={<span className="text-gray-300">—</span>} />
                </td>
                {/* 개념 — 이 저장소의 개념 배지 그대로(compact). 순번·유형은 사전에서 채우고,
                    사전이 없으면 이름만 선다. **누를 수 없다** — 행 전체가 이미 클릭 대상이다. */}
                <td className="p-3 text-xs text-gray-600">
                  {conceptPairs.length === 0 ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1">
                      {conceptPairs.map((c, i2) => {
                        const mc = matchConcept(dict, c.id);
                        return (
                          <ConceptBadge
                            key={c.id ?? `${c.name}-${i2}`}
                            name={c.name}
                            order={mc?.order ?? null}
                            type={mc?.concept_type ?? null}
                            compact
                          />
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap p-3 text-center text-gray-900">
                  {it.difficulty == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span
                      title={`${it.difficulty} (${DIFFICULTY_LABEL[it.difficulty] || '—'})`}
                      /* 모양은 여기서, 색은 공용 값에서(problemApi.difficultyBadgeClass).
                         표는 원(size-6), 카드는 알약 — 같은 색을 다른 모양으로 두른다. */
                      className={`inline-flex size-6 items-center justify-center rounded-full text-xs font-medium ${difficultyBadgeClass(it.difficulty)}`}
                    >
                      {it.difficulty}
                    </span>
                  )}
                </td>
                {showAnalysis && (
                  <td className="whitespace-nowrap p-3">
                    {st
                      ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                )}
                <td className="whitespace-nowrap p-3 text-xs text-gray-600">
                  {it.author || <span className="text-gray-300">—</span>}
                </td>
                {/* 행 클릭이 곧 [열기]라, 버튼을 누른 것이 여는 것으로도 읽히면 안 된다 */}
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-wrap items-center gap-0.5">
                    {onSelect && (
                      <RowAction icon={FolderOpen} label="열기" subject={subject} onClick={() => onSelect(id)} />
                    )}
                    {onPreview && (
                      <RowAction
                        icon={Eye}
                        label="미리보기"
                        subject={subject}
                        title="본문을 수식까지 렌더해 봅니다"
                        onClick={() => onPreview(it)}
                      />
                    )}
                    {/* 비어 있는 것이 없으면 분석이 채울 것도 없다 — 완비된 행에는 버튼을 두지 않는다 */}
                    {onRequestAnalysis && needs.length > 0 && (
                      <RowAction
                        icon={Sparkles}
                        label={inFlight ? '요청 중…' : '분석 요청'}
                        subject={subject}
                        tone="violet"
                        disabled={inFlight || inProgress}
                        title={inProgress ? '이미 분석이 진행 중입니다' : '비어 있는 항목을 AI가 채웁니다'}
                        onClick={() => onRequestAnalysis(it)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default ProblemTable;
